/**
 * Phase 19 — optimizer rules for hours, texts, gateways and (Phase 22) lists.
 * The Flow Optimizer's rule STRUCTURE (an ALL/ANY group of conditions over its
 * metric ids, judged over the rule's own date range) with each surface's own
 * actions. Unlike the Flow Optimizer (a missing metric reads 0), a rule here
 * is three-valued: a condition with no data is UNKNOWN and an unknown rule
 * never acts — missing data is never "bad". Phase 22: every surface also has
 * EXCLUDE (out of the ranking, "not judged yet"), which fires on unknown too —
 * the old volume floors are its default rule, "Not enough data".
 * For Media Devoted | Created By Ciro Autiero
 */

export type DropGenRuleOp = "<" | "<=" | "=" | "!=" | ">" | ">=";

/** The Flow Optimizer's score-ranking operators (12/10/2026). */
export type DropGenRankingOp = "highest" | "lowest" | "top_percent" | "bottom_percent";

export type DropGenRuleNode =
  | { kind: "group"; op: "AND" | "OR"; children: DropGenRuleNode[] }
  | { kind: "not"; child: DropGenRuleNode }
  | { kind: "condition"; metric: string; op: DropGenRuleOp; value: number }
  /** Score ranking — the item's raw score (its formula's weighted total) among the ranked items. */
  | { kind: "ranking"; metric: "rawScore"; op: DropGenRankingOp; percent?: number }
  /** The item carries / does not carry a Tag Rules tag of its kind (Hours, Texts, Gateways, Lists — Phase 25). */
  | { kind: "tag"; tagName: string; negate?: boolean };

/** What a surface's rules may contain. Phase 25: every optimizer surface may test its items' tags; Phase 26: hour
 *  rules may scope a Block to the groups' lists by tag. */
export interface DropGenRuleCheck {
  allowTags: boolean;
  allowScope?: boolean;
  /** The metric ids a condition may read; absent = the Flow formula metrics (a route test on its drops adds SMSWiser's). */
  metrics?: readonly string[];
}
export const DROP_GEN_NO_TAGS_CHECK: DropGenRuleCheck = { allowTags: false };
export const DROP_GEN_TAGS_CHECK: DropGenRuleCheck = { allowTags: true };
/** Hour rules' check — tags, and a Block's list scope (Phase 26). */
export const DROP_GEN_HOUR_RULE_CHECK: DropGenRuleCheck = { allowTags: true, allowScope: true };

/** Phase 22 — out of the ranking: no rank, no other rule judges it (an hour still gets the test drop). */
export const DROP_GEN_EXCLUDE_ACTION = "exclude" as const;
export const DROP_GEN_HOUR_RULE_ACTIONS = ["exclude", "no_extra", "block", "extra_first", "reduce_slots"] as const;
export const DROP_GEN_TEXT_RULE_ACTIONS = ["exclude", "retire", "proven", "retire_group"] as const;
export const DROP_GEN_GATEWAY_RULE_ACTIONS = ["exclude", "leave_pool", "last_resort", "use_rest_of_day"] as const;
export const DROP_GEN_LIST_RULE_ACTIONS = ["exclude"] as const;
/** A rule's date range that follows the formula's first range (resolved before judging). */
export const DROP_GEN_FIRST_RANGE = "first-range" as const;

export type DropGenHourRuleAction = (typeof DROP_GEN_HOUR_RULE_ACTIONS)[number];
export type DropGenTextRuleAction = (typeof DROP_GEN_TEXT_RULE_ACTIONS)[number];
export type DropGenGatewayRuleAction = (typeof DROP_GEN_GATEWAY_RULE_ACTIONS)[number];
export type DropGenListRuleAction = (typeof DROP_GEN_LIST_RULE_ACTIONS)[number];

/** Phase 26 — which groups a Block acts on, by their list of the day's tags (hour rules, action block). */
export interface DropGenRuleScope {
  /** only_tagged = lists carrying one of the tags are blocked · except_tagged = every list but those. */
  lists: "only_tagged" | "except_tagged";
  tags: string[];
}
export const DROP_GEN_SCOPE_LISTS = ["only_tagged", "except_tagged"] as const;

export interface DropGenRule<A extends string = string> {
  id: string;
  name: string;
  enabled: boolean;
  /** Phase 26 — a Block's list scope; absent = every list. */
  scope?: DropGenRuleScope;
  /** The rule's own window: a whole-day preset, "custom" with its bounds, or
   *  "first-range" (the formula's first range — the default exclude rule). */
  dateRange: string;
  dateRangeCustomFrom?: string;
  dateRangeCustomTo?: string;
  when: DropGenRuleNode;
  action: A;
  /** Parameters of the reduce_slots action. */
  reduceSlots?: { count: number; minRemaining: number };
}

/** What one rule said about one hour / text / prompt group / gateway. */
export interface DropGenRuleHit {
  ruleId: string;
  name: string;
  action: string;
  /** fired = the rule holds and acts (exclude: also when it cannot be judged) · unknown = a value is missing, it does not act. */
  state: "fired" | "unknown";
  /** The numbers behind it, in words ("ROI −18.20 < 0 over last 7 days"). */
  reason: string;
}

/** 21 = 20 of the client's own + the default "Not enough data". */
export const DROP_GEN_RULE_LIMITS = { rules: 21, conditions: 12, depth: 3, tagName: 80, scopeTags: 10 } as const;
